import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Button,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import { Logout as LogoutIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import { useDecodedToken } from "../Services/Routes/APP/PrivateRoute";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";

import { RoutesUrls } from "../Services/Routes/APP";
import UploadCSV from "./UploadCSV";
import NotificationPanel from "./NotificationPanel";

// styling

const useStyles = makeStyles({
  hiddenScrollBar: {
    padding: "1rem",
    overflowY: "scroll",
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  notificationDropdownBox: {
    "& .MuiPaper-root": {
      height: "fit-content",
      maxHeight: "50%",
      padding: "5%, 15%",
      borderRadius: 5,

      width: "27%",
      "@media (max-width: 960px)": {
        width: "35%",
      },
      "@media (max-width: 780px)": {
        width: "40%",
      },
      "@media (max-width: 660px)": {
        width: "50%",
      },
      "@media (max-width: 480px)": {
        width: "60%",
      },
      overflowY: "scroll",
      "-ms-overflow-style": "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  notificationListItem: {
    whiteSpace: "break-spaces",
    margin: "0.25em",
    padding: 0,
  },
  notificationText: {
    fontSize: "0.8rem",
    lineHeight: 1,
  },
});

const CustomAppBar = ({
  handleDrawerToggle,
  cableConnectionStatus,
  newNotification,
  clearNewNotification,
  show,
  sideBarMenuShow,
}) => {
  const user = useDecodedToken();

  const location = useLocation();
  let navigationsArray = location?.pathname?.split("/") || [];
  navigationsArray?.reverse().pop();

  const urlNavPoints = navigationsArray;
  const urlEndingPoint = urlNavPoints[0];
  urlNavPoints?.reverse();
  const urlStartPoint = navigationsArray[0];

  const [windowWidth, setWindowWidth] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertIcon, setAlertIcon] = useState("error");
  const [loaderOpen, setLoaderOpen] = useState(false);

  const [showAddNodeButton, setShowAddNodeButton] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(null);

  const open = Boolean(anchorEl);
  const theme = useTheme();
  const navigate = useNavigate();

  //csvModalHandler
  const handleCsvModalToggle = (toggleState) => {
    setCsvModalOpen(toggleState);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  //menu close handler

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logoutHandler = () => {
    localStorage.clear();
    navigate(RoutesUrls?.root);
  };
  const addHandler = () => {
    const navigationUrl = location.pathname + "/add";
    navigate(navigationUrl);
  };

  useEffect(() => {
    switch (urlStartPoint) {
      case "admin":
        if (urlEndingPoint === "ministry") {
          setShowAddNodeButton(true);
        } else {
          setShowAddNodeButton(false);
        }
        break;
      case "ministry":
        if (urlEndingPoint === "provincial") {
          setShowAddNodeButton(true);
        } else {
          setShowAddNodeButton(false);
        }
        break;
      case "province":
        if (urlEndingPoint === "districts") {
          setShowAddNodeButton(true);
        } else {
          setShowAddNodeButton(false);
        }
        break;
      case "district":
        if (urlEndingPoint === "clinics") {
          setShowAddNodeButton(true);
        } else {
          setShowAddNodeButton(false);
        }
        break;
      default:
        setShowAddNodeButton(false);
        break;
    }
  }, [urlStartPoint, urlEndingPoint]);

  useEffect(() => {
    const updateWindowDimensions = () => {
      const windowWidth = window.innerWidth;
      setWindowWidth(windowWidth);
    };

    window.addEventListener("resize", updateWindowDimensions);

    return () => window.removeEventListener("resize", updateWindowDimensions);
  }, []);

  return (
    <Box
      component={"header"}
      sx={{
        p: { xs: 1, sm: 1, md: 3 },
      }}
    >
      <Paper
        elevation={1}
        sx={{
          borderRadius: 1,
          display: show || windowWidth < 900 ? "block" : "none",
        }}
      >
        <Box padding={1} display={"flex"} justifyContent={"space-between"}>
          <Box alignItems={"center"} display={"flex"}>
            <Box display={"flex"} alignItems={"center"}>
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={() => handleDrawerToggle()}
                sx={{
                  margin: 0,
                  marginRight: { xs: "0.1rem", sm: "0.25rem" },
                  display: { md: sideBarMenuShow ? "inline-flex" : "none" },
                }}
              >
                <MenuIcon color="primary" />
              </IconButton>
              <Typography
                variant="h5"
                width="fit-content"
                sx={{
                  margin: { xs: "auto 0.1rem", sm: "auto 0.25rem" },
                  fontWeight: "bold",
                  color: theme.palette.text.light,
                  textTransform: "capitalize",
                }}
              >
                {urlEndingPoint === "CreateRequest"
                  ? "Create Request"
                  : urlEndingPoint && urlEndingPoint === "issueManually"
                  ? "Issue Manually"
                  : urlEndingPoint && urlEndingPoint === "unitsUsed"
                  ? "Units Used"
                  : urlEndingPoint && urlEndingPoint === "changePassword"
                  ? "Change Password"
                  : urlEndingPoint && urlEndingPoint === "receiveInventory"
                  ? "Receive Inventory"
                  : urlEndingPoint && urlEndingPoint === "nodeView"
                  ? "Node View"
                  : urlEndingPoint && urlEndingPoint === "SeeMore"
                  ? "See More"
                  : urlEndingPoint}
              </Typography>
            </Box>
          </Box>
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            {user?.role === "ministry" && urlEndingPoint === "dashboard" ? (
              <Box display={"flex"} alignItems={"center"}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => handleCsvModalToggle(true)}
                  sx={{
                    margin: { xs: "auto 0.1rem", sm: "auto 0.25rem" },
                    maxHeight: 40,
                    minWidth: 120,
                    display: { xs: `none`, sm: "flex" },
                  }}
                >
                  Create Nodes
                </Button>
              </Box>
            ) : null}
            {showAddNodeButton ? (
              <Box
                display={"flex"}
                alignItems={"center"}
                margin={{ xs: "auto 0.1rem", sm: "auto 0.25rem" }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  onClick={addHandler}
                  sx={{
                    maxHeight: 40,
                    minHeight: 40,
                    maxWidth: 180,
                    minWidth: 180,
                    padding: 0.8,

                    display: { xs: `none`, sm: "flex" },
                  }}
                  startIcon={<AddIcon />}
                >
                  {user?.role === "super_admin"
                    ? "Add ministry"
                    : "Add Node Manually"}
                </Button>
              </Box>
            ) : null}
            {user.role !== "super_admin" ? (
              <NotificationPanel
                cableConnectionStatus={cableConnectionStatus}
                newNotification={newNotification}
                clearNewNotification={clearNewNotification}
              />
            ) : null}
            <Typography
              sx={{
                textTransform: "capitalize",
                marginLeft: { xs: "auto 0.1rem", sm: "auto 0.25rem" },
                width: "fit-content",
                display: { xs: "none", sm: "block" },
              }}
            >
              {user?.name}
            </Typography>
            <Box
              component={IconButton}
              onClick={handleClick}
              marginLeft={{ xs: "0.1rem", sm: "0.25rem" }}
            >
              <Avatar sx={{ width: "40px", height: "40px" }} src=""></Avatar>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={logoutHandler}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
              {user?.role === "ministry" ? (
                <MenuItem
                  onClick={() => {
                    handleCsvModalToggle(true);
                  }}
                  sx={{
                    display: { xs: "flex", sm: "none" },
                  }}
                >
                  <ListItemIcon>
                    <UploadFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Upload File</ListItemText>
                </MenuItem>
              ) : null}

              {showAddNodeButton ? (
                <MenuItem
                  onClick={addHandler}
                  sx={{
                    display: { xs: "flex", sm: "none" },
                  }}
                >
                  <ListItemText>
                    {user?.role === "super_admin"
                      ? "Add ministry"
                      : "Add Node Manually"}
                  </ListItemText>
                </MenuItem>
              ) : null}
            </Menu>
          </Box>
        </Box>
      </Paper>
      <UploadCSV
        showCsvModal={csvModalOpen}
        handleCsvModalToggle={handleCsvModalToggle}
      />
    </Box>
  );
};

export default CustomAppBar;
